<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(defineProps<{
  handler: string;                    // route name or path starting with '/'
  emphasis?: boolean;
  query?: Record<string, any>;
  block?: boolean;
  disabled?: boolean;
}>(), { emphasis: false, block: false, disabled: false });

const to = computed(() => {
  const hasQuery = props.query && Object.keys(props.query).length > 0;
  const isPath = props.handler.startsWith("/");
  if (!hasQuery) return props.handler;
  return isPath ? { path: props.handler, query: props.query }
                : { name: props.handler, query: props.query };
});
</script>

<template>
  <RouterLink
    :to="to"
    class="menu"
    :class="{ emphasis, block, disabled }"
    :aria-disabled="disabled ? 'true' : undefined"
    :tabindex="disabled ? -1 : undefined"
  >
    <slot>Name Missing</slot>
  </RouterLink>
</template>

<style scoped>
.menu {
  /* button-like reset */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .4rem;

  height: 40px;
  padding: 0 14px;
  margin-bottom: 4px;
  border-radius: 8px 0 8px 0;

  background-color: var(--color-menu, #f8f9fb);
  color: var(--color-text, #111);

  border-top: 1px solid var(--color-menu-divider-2, #dfe3ea);
  border-bottom: 2px solid var(--color-menu-divider, #c8cfda);
  border-left: 0;
  border-right: 0;

  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  line-height: 1;
  cursor: pointer;

  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    transform 80ms ease,
    box-shadow 120ms ease;
}

.menu.block { display: flex; width: 100%; }

.menu:hover {
  background-color: var(--color-menu-divider, #e9edf3);
}

/* Emphasis variant */
.menu.emphasis {
  background-color: var(--color-menu-emp, #e6f0ff);
  border-top: 1px solid var(--color-menu-emp-divider, #c9ddff);
  border-bottom: 2px solid var(--color-menu-emp-divider-2, #a8c8ff);
  color: var(--color-text, #111);
}
.menu.emphasis:hover {
  background-color: var(--color-menu-emp-divider-2, #a8c8ff);
}

/* Active/pressed + focus states */
.menu:active { transform: translateY(1px); }
.menu:focus-visible {
  outline: 2px solid var(--accent, #2563eb);
  outline-offset: 2px;
}

/* RouterLink active helpers (optional) */
.menu.router-link-active {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent, #2563eb) 18%, transparent);
}
.menu.router-link-exact-active {
  background-color: color-mix(in srgb, var(--color-menu, #f8f9fb) 70%, var(--accent, #2563eb) 30%);
}

/* Disabled */
.menu.disabled {
  opacity: .6;
  cursor: not-allowed;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .menu { transition: none; }
}

</style>
